import {
  Box,
  ScrollArea,
  Button,
  Container,
  Grid,
  Text,
} from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import { Dispatch, SetStateAction } from "react";
import useSWRInfinite from "swr/infinite";
import WatsonApi from "../../../backends/WatsonApi";
import { Channel, ChannelButton } from "./Channel";
import { MessagingListRulesQuery } from "../../modals/Messaging/MessagingListRules";
import RoundIcon from "../../admin/utilities/RoundIcon";
import styles from "../styles/ChannelsList.module.scss";
import classNames from "classnames";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { useStores } from "../../../stores/useStores";
import { OrgFilter } from "./ListFilter";
import { ChatAvatar } from "./ChatAvatar";
import { useMessagingNotEnabledCallout } from "../MessagingNotEnabledCallout";

export type IChannel = {
  id: number;
  title: string;
  description: string;
  type: "message" | "broadcast";
  branding: {
    type: "image" | "fontawesome" | "text";
    alt?: string;
    value: string;
  };
  timestamp: string;
  has_unread: boolean;
  owner_user_id: string;
};

export function useChannels(
  initialChannels,
  relevancyOrgId: string | undefined,
) {
  return useSWRInfinite(
    (pageIndex) =>
      JSON.stringify({
        page: pageIndex,
        key: "message-channels",
        relevancyOrgId,
      }),
    async (key) => {
      const parsedKey = JSON.parse(key);
      const client = await WatsonApi();
      const nextPage = parsedKey.page + 1;
      const q = parsedKey.relevancyOrgId
        ? {
            is_relevant_to_org: parsedKey.relevancyOrgId,
          }
        : {
            exclude_owner_org: "root",
          };
      const result = await client.apis.message_list.message_list_list({
        ...q,
        page: nextPage,
        page_size: 12,
      });
      const parsed = JSON.parse(result.data);
      return {
        channels: parsed.data,
        total: parsed.meta.total,
      };
    },
    {
      fallbackData: [initialChannels],
      revalidateOnFocus: false,
      revalidateIfStale: true,
      revalidateOnMount: false,
      revalidateFirstPage: false,
      keepPreviousData: true,
    },
  );
}

export default function ChannelsList(props: {
  channels: IChannel[];
  totalChannels: number;
  currentChannel: IChannel | undefined;
  setCurrentChannel: Dispatch<SetStateAction<IChannel | undefined>>;
  showConversation: boolean;
  setRecipientUsers: Dispatch<SetStateAction<IUser[]>>;
  setRecipientRules: Dispatch<SetStateAction<MessagingListRulesQuery[]>>;
  setShowConversation: Dispatch<SetStateAction<boolean>>;
  startConversation: () => void;
  isLoadingChannels: boolean;
  isValidatingChannels: boolean;
  setChannelsSize: Dispatch<SetStateAction<number>>;
  mutateChannels: any;
  filterOrganization: OrgFilter | null;
  markChannelAsRead: (channelId: IChannel["id"]) => void;
}) {
  const { userStore, organizationStore } = useStores();
  const showMessagingNotEnabledCallout = useMessagingNotEnabledCallout();

  const showCreateMessageButton =
    (organizationStore.userHasRole &&
      organizationStore.organization.billing_products?.messaging) ||
    showMessagingNotEnabledCallout;

  const isMoreChannels =
    !props.isLoadingChannels && props.totalChannels > props.channels.length;

  const containerClassName = classNames({
    [styles.container]: true,
    [styles.containerHidden]: !!props.currentChannel,
  });

  useAsyncEffect(async () => {
    if (props.currentChannel?.has_unread) {
      const client = await WatsonApi();
      await client.apis.users.users_activity_mark_all_read({
        user_pk: userStore.id,
        data: {
          message_list_id: props.currentChannel.id,
        },
      });
      if (props.channels) {
        props.markChannelAsRead(props.currentChannel.id);
      }
    }
  }, [props.currentChannel]);

  return (
    <Box
      flexBasis={{
        initial: "100%",
        md: props.showConversation ? "50%" : "100%",
      }}
      height={"500px"}
      style={{ backgroundColor: "var(--gray-2)", boxShadow: "var(--shadow-4)" }}
      p={"2"}
      className={containerClassName}
    >
      <Grid rows={"2"} height={"100%"} style={{ gridTemplateRows: "1fr auto" }}>
        <ScrollArea scrollbars={"vertical"}>
          <Container mr={"5"}>
            {props.filterOrganization && (
              <Box display={"inline-block"}>
                <Text weight={"bold"} size={"4"}>
                  {props.filterOrganization.title}
                </Text>
              </Box>
            )}
            {props.filterOrganization && (
              <Box>
                <Text>
                  Showing all messages related to&nbsp;
                  {props.filterOrganization.title}
                </Text>
              </Box>
            )}
            {showCreateMessageButton && (
              <ChannelButton
                isHighlighted={false}
                onClick={props.startConversation}
                hasUnread={false}
                avatar={
                  <ChatAvatar
                    branding={{
                      type: "fontawesome",
                      value: "fas fa-plus",
                      alt: "Create Message List",
                    }}
                    avatarProps={{ color: "blue" }}
                  />
                }
                title={"Create New Message"}
                description={"Send a message to individuals or a group."}
              />
            )}
            {props.channels.length > 0 ? (
              <>
                {props.channels.map((list) => {
                  return (
                    <Channel
                      channel={list}
                      currentChannelId={props.currentChannel?.id}
                      setCurrentChannel={(channel) => {
                        props.setCurrentChannel(channel);
                        props.setShowConversation(!!channel);
                        props.setRecipientUsers([]);
                        props.setRecipientRules([]);
                      }}
                      key={list.id}
                    />
                  );
                })}
              </>
            ) : (
              <Box pt={"2"} style={{ textAlign: "center" }}>
                <Box>
                  <RoundIcon className={styles.roundButton}>
                    <FontAwesome prefix={"fas"} name={"fa-envelope"} />
                  </RoundIcon>
                </Box>
                <Box pt={"1"}>
                  <Text>You do not have any messages.</Text>
                </Box>
              </Box>
            )}
          </Container>
        </ScrollArea>
        {isMoreChannels && (
          <Box pt={"3"} style={{ textAlign: "center" }}>
            <Button
              loading={props.isValidatingChannels}
              onClick={() => props.setChannelsSize((s) => s + 1)}
            >
              Load More
            </Button>
          </Box>
        )}
      </Grid>
    </Box>
  );
}
